import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvConfig, ShopTone } from '@digilize/shared/definitions/src';
import { getTranslationSuffix } from '@digilize/shared/utils/helpers/src';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens/src';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { ShopToneService } from './shop-tone.service';
import { TRANSLATION_BASE_TONE } from './translation-base-tone';

/** Filename marker per register, for the overlay that carries the one the base is not written in. */
const MARKER: Record<ShopTone, string> = {
  [ShopTone.Formal]: '-formal',
  [ShopTone.Informal]: '-informal',
};

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  private envConfig: EnvConfig = inject(ENV_CONFIG);
  private shopTone = inject(ShopToneService);
  private baseTone = inject(TRANSLATION_BASE_TONE);
  getTranslation(lang: string) {
    let obs: Observable<any>[] = [];
    const SUFFIX = getTranslationSuffix(this.envConfig);
    const isLazyLoaded = lang.includes('/');

    if (!isLazyLoaded) {
      obs.push(this.http.get<Translation>(`assets/i18n/common/${lang}${SUFFIX}`));
      obs.push(this.http.get<Translation>(`assets/i18n/${lang}${SUFFIX}`));
      obs.push(this.http.get<Translation>(`assets/i18n/common/alerts-${lang}${SUFFIX}`));
    } else {
      obs.push(this.http.get<Translation>(`assets/i18n/modules/${lang}${SUFFIX}`));
    }

    /* The other register is an overlay, not a second full translation: it carries only the keys that
       address the customer, and is merged last so it wins over the base. Requested only when the shop
       disagrees with the register the app's base is written in (`TRANSLATION_BASE_TONE`), so an app
       whose base already matches - which for pauly-shop and pauly-auth is every shop until an admin
       opts out - pays no extra request. A key the overlay does not cover falls back to the base. */
    const tone = this.shopTone.current;

    if (tone !== this.baseTone) {
      const marker = MARKER[tone];
      const overlay = isLazyLoaded
        ? [`assets/i18n/modules/${lang}${marker}${SUFFIX}`]
        : [
            `assets/i18n/common/${lang}${marker}${SUFFIX}`,
            `assets/i18n/${lang}${marker}${SUFFIX}`,
            `assets/i18n/common/alerts-${lang}${marker}${SUFFIX}`,
          ];

      /* Most of these do not exist - English has one register, and no module scope has an overlay
         yet - so a 404 has to be an empty merge rather than a failure that takes the language down. */
      overlay.forEach((url) => obs.push(this.http.get<Translation>(url).pipe(catchError(() => of({} as Translation)))));
    }

    return forkJoin([...obs]).pipe(
      map((translationsArr) => {
        const translations = translationsArr.reduce((acc, curr) => {
          return this.mergeRecursively(acc, curr);
        }, {});
        return translations;
      })
    );
  }

  mergeRecursively(obj1, obj2) {
    const merged = {};

    for (let key in obj1) {
      if (obj1.hasOwnProperty(key)) {
        // If the key exists in both objects and both values are objects, merge them recursively
        if (
          obj2.hasOwnProperty(key) &&
          typeof obj1[key] === 'object' &&
          typeof obj2[key] === 'object' &&
          !Array.isArray(obj1[key]) &&
          !Array.isArray(obj2[key])
        ) {
          merged[key] = this.mergeRecursively(obj1[key], obj2[key]);
        } else if (obj2.hasOwnProperty(key)) {
          // If the key exists in both but not both are objects, take the value from the second object
          merged[key] = obj2[key];
        } else {
          // Otherwise, take the value from the first object
          merged[key] = obj1[key];
        }
      }
    }

    // Add any keys from the second object that aren't in the first object
    for (let key in obj2) {
      if (obj2.hasOwnProperty(key) && !merged.hasOwnProperty(key)) {
        merged[key] = obj2[key];
      }
    }

    return merged;
  }
}
